import React, { useState } from "react";
import { Button, PaddedContainer } from "@disco/disco_core";

import { LeadGenActionButton } from "../LeadGen/LeadGenAction";
import useWidgetOffer from "../../hooks/useWidgetOffer";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import useShopTheme from "../../hooks/useShopTheme";
import accessibilityProvider from "../../utils/accessibilityProvider";
import { WIDGET_ELEMENT_CLICK_TARGET } from "../../event_conf";
import DiscoCollectiveButton from "./DiscoCollectiveButton";
import useExperience from "../../hooks/useExperience";
import useDimensions from "../../hooks/useDimensions";
import { WIDGET_TYPE } from "../../conf";
import "./engage-offer-footer.css";

export const NextOfferButton = ({
	isActionAnimating,
	btnProps: btnPropsProps,
	onClick,
}) => {
	const { activeWidgetType } = useExperience();
	const { isLargeWidget } = useDimensions();
	const track = useTrack();
	const {
		isOfferClaimed,
		showNextOffer,
		isLastOffer,
		defaultOfferEventProps,
	} = useWidgetOffer();

	const shouldStyleBtn = isOfferClaimed && !isActionAnimating;

	const btnProps = {
		style: {
			"--border-color": "transparent",
			boxShadow: "unset",
			"--curve": "var(--disco-curve-tn)",
			flex: "0 0 auto",
			whiteSpace: "nowrap",
			...(shouldStyleBtn && btnPropsProps.style),
			...(!isLargeWidget && {
				"--h-padding": "var(--disco-spacing-tn)",
			}),
		},
		secondary: true,
	};

	if (
		isLastOffer &&
		isOfferClaimed &&
		activeWidgetType === WIDGET_TYPE.ENGAGE
	) {
		return (
			<DiscoCollectiveButton {...btnProps}>
				See more
			</DiscoCollectiveButton>
		);
	}

	const handleNextOffer = () => {
		track(eventTypes.WIDGET_ELEMENT_CLICK, {
			...defaultOfferEventProps,
			initiatingTarget: WIDGET_ELEMENT_CLICK_TARGET.INLINE_NAV_FORWARD,
		});

		if (!isLastOffer) {
			showNextOffer();
		}

		onClick?.();
	};

	const getButtonText = () => {
		if (isOfferClaimed && !isActionAnimating) {
			if (isLastOffer) {
				return "See more";
			}

			return "Next offer";
		}

		return "No thanks";
	};

	return (
		<Button
			{...btnProps}
			className="transparent-bg"
			onClick={handleNextOffer}
		>
			{getButtonText()}
		</Button>
	);
};

const EngageOfferFooter = ({ setShouldRenderOffersSlot }) => {
	const [isActionAnimating, setIsActionAnimating] = useState(false);
	const {
		activeOffer,
		activeOfferIdx,
		isOfferClaimed,
		isLastOffer,
		claimOffer,
		defaultOfferEventProps,
	} = useWidgetOffer();

	const {
		backgroundColor: shopBackgroundColor,
		foregroundColor: shopForegroundColor,
		isConfigured: isShopThemeConfigured,
	} = useShopTheme();

	const btnProps = {
		secondary: true,
		rounded: false,
		centerContent: true,
		...accessibilityProvider({
			role: "button",
		}),
		style: {
			"--bg": isShopThemeConfigured
				? shopBackgroundColor
				: "var(--disco-facebook)",
			"--color": isShopThemeConfigured
				? shopForegroundColor
				: "var(--disco-text-white)",
			"--border-color": "transparent",
		},
	};

	const handleOnNext = () => {
		if (isLastOffer) {
			setShouldRenderOffersSlot(false);
		}
	};

	return (
		<PaddedContainer
			style={{
				display: "flex",
				alignItems: "center",
				gap: "var(--disco-spacing-sm)",
			}}
		>
			<LeadGenActionButton
				brand={activeOffer}
				isOfferClaimed={isOfferClaimed}
				btnProps={btnProps}
				defaultEventProps={defaultOfferEventProps}
				onOfferClaimed={claimOffer}
				activeIdx={activeOfferIdx}
				isActionAnimating={isActionAnimating}
				setIsActionAnimating={setIsActionAnimating}
				shouldApplyCtaHighlightStyle={true}
			/>

			<NextOfferButton
				isActionAnimating={isActionAnimating}
				btnProps={btnProps}
				onClick={handleOnNext}
			/>
		</PaddedContainer>
	);
};

export default EngageOfferFooter;
